import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CustomFieldType, DocumentType} from '@ee/common/enums';
import {FormControl} from '@angular/forms';
import {CustomField, CustomFieldOption, fieldTypeToIcon, resolveMultiSelectOptions} from '@ee/common/models';
import {Subscription} from 'rxjs';
import {DateTime} from 'luxon';

@Component({
  selector: 'ee-field',
  template: `
    @switch (field.type) {
      @case (fieldTypes.DATE) {
        <mat-form-field class="w-full" [class]="formClass">
          <mat-label>{{field.label}}</mat-label>
          <input matInput [formControl]="$any(control)" [matDatepicker]="picker"
                 [required]="field.required">
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
          @if (control?.hasError('matDatepickerParse')) {
            <mat-error>Enter a date as MM/DD/YYYY.</mat-error>
          } @else if (control?.hasError('required')) {
            <mat-error>Please enter a value.</mat-error>
          }
          @if (!hideHint) {
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          }
        </mat-form-field>
      }
      @case (fieldTypes.DECIMAL) {
        <mat-form-field class="w-full" [class]="formClass">
          <mat-label>{{field.label}}</mat-label>
          @if (isMonetary) {
            <span matPrefix>$ &nbsp;</span>
          }
          <input matInput type="number" [formControl]="$any(control)" [required]="field.required">
          @if (!hideHint) {
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          }
        </mat-form-field>
      }
      @case (fieldTypes.SELECT) {
        <mat-form-field class="w-full" [class]="formClass">
          <mat-label>{{field.label}}</mat-label>
          <mat-select [formControl]="$any(control)" [required]="field.required">
            @for (option of field.options; track option) {
              <mat-option [value]="option">
                {{option}}
              </mat-option>
            }
          </mat-select>
          @if (!hideHint) {
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          }
        </mat-form-field>
      }
      @case (fieldTypes.BOOLEAN) {
        <div class="w-full">
          @if (field.true_label && field.false_label) {
            <mat-radio-group class="ee" [formControl]="$any(control)">
              <mat-radio-button class="boolean-selection mr-4" value="true">
                <b>{{field.true_label}}</b>
              </mat-radio-button>
              <mat-radio-button class="boolean-selection" value="false">
                <b>{{field.false_label}}</b>
              </mat-radio-button>
            </mat-radio-group>
          } @else {
            <mat-slide-toggle class="w-full mb-4" [formControl]="$any(control)" [class]="formClass">
              {{field.label}}
            </mat-slide-toggle>
          }
        </div>
      }
      @case (fieldTypes.MULTI_SELECT) {
        <!-- fieldset/legend so assistive technology reads the question before the choices;
             without it a screen reader announces unrelated checkboxes with no group name. -->
        <fieldset class="w-full multi-select" [class]="formClass"
                  [attr.aria-invalid]="showChoiceRequiredError"
                  [attr.aria-describedby]="showChoiceRequiredError ? choiceErrorId : null">
          <legend class="multi-select__label">
            {{field.label}}@if (field.required) {<span class="multi-select__required" aria-hidden="true"> *</span>}
          </legend>
          @for (option of choiceOptions; track option.value) {
            <mat-checkbox class="multi-select__option" [checked]="isMultiSelectOptionSelected(option.value)"
                          [disabled]="control?.disabled"
                          (change)="toggleMultiSelectOption(option.value, $event.checked)">
              {{option.label}}
            </mat-checkbox>
          }
          @if (showChoiceRequiredError) {
            <div class="error multi-select__error" [id]="choiceErrorId">Select at least one option.</div>
          }
          @if (!hideHint) {
            <div class="multi-select__hint">
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </div>
          }
        </fieldset>
      }
      @case (fieldTypes.RADIO) {
        <!-- Same fieldset/legend as MULTI_SELECT: without it a screen reader announces unrelated
             radios with no group name. -->
        <fieldset class="w-full multi-select" [class]="formClass"
                  [attr.aria-invalid]="showChoiceRequiredError"
                  [attr.aria-describedby]="showChoiceRequiredError ? choiceErrorId : null">
          <legend class="multi-select__label">
            {{field.label}}@if (field.required) {<span class="multi-select__required" aria-hidden="true"> *</span>}
          </legend>
          <mat-radio-group class="radio-group" [formControl]="$any(control)">
            @for (option of choiceOptions; track option.value) {
              <mat-radio-button class="multi-select__option" [value]="option.value">
                {{option.label}}
              </mat-radio-button>
            }
            <!-- An optional field has to be returnable to blank. A radio cannot be un-picked by
                 clicking it again, so without this an optional Radio Group is irreversible once
                 answered — worse than SELECT (which has a "—" option) or MULTI_SELECT (untickable).
                 Omitted when the field is required, where blank is not a legal answer anyway. -->
            @if (!field.required) {
              <mat-radio-button class="multi-select__option" [value]="null">
                <span class="radio-group__none">— No selection —</span>
              </mat-radio-button>
            }
          </mat-radio-group>
          @if (showChoiceRequiredError) {
            <div class="error multi-select__error" [id]="choiceErrorId">Select an option.</div>
          }
          @if (!hideHint) {
            <div class="multi-select__hint">
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </div>
          }
        </fieldset>
      }
      @case (fieldTypes.TIME) {
        <mat-form-field class="w-full" [class]="formClass">
          <mat-label>{{field.label}}</mat-label>
          <input matInput [formControl]="$any(control)"
                 [required]="field.required" [ngxMatTimepicker]="matTimepicker">
          @if (!hideHint) {
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          }
          <ngx-mat-timepicker #matTimepicker [defaultTime]="'3:00 pm'"></ngx-mat-timepicker>
        </mat-form-field>
      }
      @case (fieldTypes.FILE_UPLOAD) {
        <ee-uploader class="w-full" [form]="$any(control)" [class]="formClass"></ee-uploader>
      }
      @case (fieldTypes.CALCULATED) {
        <ng-container [ngTemplateOutlet]="smartReadonly"></ng-container>
      }
      @case (fieldTypes.DATE_OFFSET) {
        <ng-container [ngTemplateOutlet]="smartReadonly"></ng-container>
      }
      @default {
        <mat-form-field class="w-full" [class]="formClass">
          <mat-label>{{field.label}}</mat-label>
          <!-- Gated on TEXT, not on "whatever reached @default". INTEGER, MULTI_TEXT, ADDRESS and
               TAGS all fall through to this branch, so binding the field's mask unconditionally
               would let a stray pattern reshape a non-text value — and a mask is destructive, since
               ngx-mask strips the mask characters from what gets saved. -->
          <input matInput [formControl]="$any(control)" [required]="field.required"
                 [mask]="textMask">
          @if (!hideHint) {
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          }
        </mat-form-field>
      }
    }
    <ng-template #smartReadonly>
      <mat-form-field class="w-full" [class]="formClass">
        <mat-label>{{field.label}}</mat-label>
        <input matInput [formControl]="$any(control)" readonly>
        <span matSuffix class="mr-2"><i [class]="fieldTypeToIcon(field.type)"></i></span>
        @if (!hideHint) {
          <mat-hint>Computed automatically.</mat-hint>
        }
      </mat-form-field>
    </ng-template>
    <ng-template #hint>
      <span>{{ field.required ? 'Required' : 'Optional' }}. </span>
      @if (field.hint) {
        <span>{{field.hint}}. </span>
      }
      @if (field.hint_more_info) {
        <span class="field-instructions-wrapper">
          Hover for <span class="field-instructions" [tp]="field.hint_more_info" tpPlacement="bottom">
            more info</span>.
        </span>
      }
    </ng-template>
  `,
  styles: [`
    @use 'components/color-palette';

    /* A grid/flex item defaults to min-width:auto, i.e. it refuses to shrink below its content. In
       the Edit Internal Fields dialog that let a long checkbox label force the auto-fill grid to size
       its tracks past the container, so the dialog scrolled sideways. Allowing the host to shrink is
       what lets the label wrap instead. */
    :host {
      min-width: 0;
    }

    .w-full {
      width: 100% !important;
    }

    .calculated-display {
      padding: 6px 0;

      .calculated-display__label {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
      }

      .calculated-display__value {
        font-size: 1rem;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.85);
      }
    }

    .boolean-selection {
      min-width: 100px;
    }

    /* Stacked, like the checkbox group — a Radio Group's options are full sentences often enough that
       laying them out in a row would truncate them. Deliberately NOT the global "ee" radio-group class:
       that one draws each option as a bordered card in a row (the Yes/No boolean pills, the plan picker),
       which stacked here became a column of big outlined cards, every one after the first indented. */
    .radio-group {
      display: flex;
      flex-direction: column;
    }

    .radio-group__none {
      color: rgba(0, 0, 0, 0.6);
      font-style: italic;
    }

    .multi-select {
      display: flex;
      flex-direction: column;
      margin-bottom: 16px;
      /* strip the browser's default fieldset chrome — the grouping is for semantics, not decoration */
      border: none;
      padding: 0;
      margin-inline: 0;
      /* A fieldset defaults to min-inline-size: min-content, so it will not shrink below its widest
         unbroken content. Inside the Edit Internal Fields grid that forced the column to 888px in a
         742px track and the dialog scrolled sideways, because a long option label never got the
         chance to wrap. This one line is what lets it. */
      min-inline-size: 0;
      min-width: 0;

      .multi-select__label {
        /* 16px, the size of the intake's other question labels (e.g. "Property Type:"): this legend
           IS the question, and reading smaller than the answers it governs made it look like a hint. */
        font-size: 1rem;
        color: var(--color-on-surface);
        margin-bottom: 6px;
        padding: 0;
      }

      /* Option labels are court-document phrases, so they can be long. mat-checkbox lays its label
         out as a flex item, which takes its full one-line width unless something bounds it — so a
         long option stretched the fieldset past the dialog (888px inside a 790px content area)
         instead of wrapping. Bound the row and let the label shrink; the box itself must not. */
      max-width: 100%;

      .multi-select__option {
        max-width: 100%;

        /* Centre the box/radio against the label (Material's default). A hand-tuned top offset on the
           label only lined up at one font size and left every option sitting low; a wrapped label now
           centres the control against the whole label, which is Material's own behaviour. */
        ::ng-deep .mdc-form-field {
          max-width: 100%;
          align-items: center;
        }

        ::ng-deep .mdc-checkbox,
        ::ng-deep .mdc-radio {
          flex: 0 0 auto;
        }

        ::ng-deep label {
          min-width: 0;
          white-space: normal;
          overflow-wrap: anywhere;
          line-height: 1.3;
        }
      }

      .multi-select__error {
        margin-top: 4px;
      }

      /* Decorative required marker, matching Material's own; the group announces requiredness
         through aria-invalid / the hint, so this is aria-hidden in the template. */
      .multi-select__required {
        color: var(--color-accent-danger-text);
      }

      .multi-select__hint {
        font-size: 75%;
        color: var(--color-on-surface-variant);
        margin-top: 4px;
      }
    }

    .field-instructions {
      color: var(--primary-red);
      text-decoration: underline;
      text-decoration-style: dashed;
      cursor: pointer;
      font-weight: 600;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FieldComponent implements OnInit, OnChanges, OnDestroy {
  fieldTypes = CustomFieldType;
  @Input() control: FormControl;
  @Input() field: CustomField;
  @Input() compact = false;
  @Input() hideFromIntake = false;
  @Input() hideHint = false;

  /**
   * The MULTI_SELECT checkboxes to render: the field's options followed by any stored value that
   * no longer matches one (Req 5.5). Held as a field rather than a getter so it isn't rebuilt on
   * every change-detection tick, and so unticking an orphan leaves its box on screen.
   */
  choiceOptions: CustomFieldOption[] = [];

  /** The field key {@link choiceOptions} was built for, so a rebuild knows whether the carried
   *  orphans still belong to the field being rendered. */
  private choiceOptionsKey: string | null = null;

  private subs: Subscription[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.convertDateValueIfNeeded();
    this.rebuildChoiceOptions();
    this.resubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['field']) {
      this.convertDateValueIfNeeded();
      this.rebuildChoiceOptions();
      // The parent may have swapped the control itself (the eviction section forms rebuild their
      // custom_fields groups), which would otherwise leave a live subscription to the abandoned
      // control and none at all for the new one.
      this.resubscribe();
    }
  }

  /** Tears down the value-change watchers and re-establishes them for the current control/field. */
  private resubscribe(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
    if (!this.control) {
      return;
    }

    // Watch for control value changes and convert date strings to UTC Date objects
    if (this.field?.type === CustomFieldType.DATE) {
      this.subs.push(
        this.control.valueChanges.subscribe(() => {
          this.convertDateValueIfNeeded();
        })
      );
    }

    // A value patched in after render (e.g. a case loaded asynchronously) can carry stored values
    // that aren't in the current options; surface those rather than dropping them silently.
    if (this.rendersChoiceGroup) {
      this.subs.push(
        this.control.valueChanges.subscribe(() => {
          this.mergeStoredValuesIntoChoiceOptions();
          // MULTI_SELECT is the one branch that renders with [checked]/(change) rather than
          // [formControl], so it gets no ControlValueAccessor view sync. Without an unconditional
          // markForCheck an external setValue/patchValue/reset (a conditional rule disabling a
          // section, clearForm) leaves an OnPush host painting the old ticks. RADIO does bind
          // [formControl], but it still needs the merge above, and marking is harmless.
          this.cdr.markForCheck();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  /**
   * Converts date string values to UTC Date objects to prevent timezone shifts
   * Extracts the date components directly from the ISO string without timezone conversion
   */
  private convertDateValueIfNeeded(): void {
    if (!this.control || !this.field || this.field.type !== CustomFieldType.DATE) {
      return;
    }

    const currentValue = this.control.value;
    if (!currentValue) {
      return;
    }

    // If value is already a Date object, ensure it represents the date correctly in local timezone
    // Material datepicker displays dates in local time, so we need to ensure the Date object
    // represents the correct date when displayed locally
    if (currentValue instanceof Date) {
      const year = currentValue.getFullYear();
      const month = currentValue.getMonth();
      const day = currentValue.getDate();
      
      // Create a new Date object in local timezone at midnight for the same date
      // This ensures Material datepicker displays the correct date
      const localDate = new Date(year, month, day);
      
      // Only update if the date components differ (indicating timezone shift)
      const currentYear = currentValue.getFullYear();
      const currentMonth = currentValue.getMonth();
      const currentDay = currentValue.getDate();
      
      if (currentYear !== year || currentMonth !== month || currentDay !== day) {
        this.control.setValue(localDate, { emitEvent: false });
        this.cdr.markForCheck();
      }
      return;
    }

    // If value is a string, extract date components directly from the string
    if (typeof currentValue === 'string') {
      try {
        // Parse the ISO string to extract the date part (YYYY-MM-DD)
        // This preserves the date regardless of timezone offset
        // Example: "2025-12-02T00:00:00.000-06:00" -> extract "2025-12-02"
        const dateMatch = currentValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
          const day = parseInt(dateMatch[3], 10);
          
          // Create a Date object in local timezone at midnight
          // Material datepicker displays dates in local time, so we need a local Date object
          // This ensures the date picker shows the correct date (e.g., Dec 2, 2025)
          const localDate = new Date(year, month, day);
          this.control.setValue(localDate, { emitEvent: false });
          this.cdr.markForCheck();
        } else {
          // Fallback to Luxon parsing if regex doesn't match
          const dateTime = DateTime.fromISO(currentValue);
          if (dateTime.isValid) {
            // Extract date components from the parsed DateTime
            // Parse without timezone conversion to preserve the date as written
            const dateOnly = currentValue.split('T')[0];
            const dateParts = dateOnly.split('-');
            if (dateParts.length === 3) {
              const year = parseInt(dateParts[0], 10);
              const month = parseInt(dateParts[1], 10) - 1;
              const day = parseInt(dateParts[2], 10);
              // Create a Date object in local timezone
              const localDate = new Date(year, month, day);
              this.control.setValue(localDate, { emitEvent: false });
              this.cdr.markForCheck();
            }
          }
        }
      } catch (error) {
        console.warn('Error converting date string to local Date:', error, currentValue);
      }
    }
  }

  /**
   * Whether to show the "pick one" message. A required group is invalid while empty (Req 5.3), but
   * only says so once the user has been near it — otherwise every unfilled form opens shouting.
   */
  /** Id of the group's error message, so the fieldset can point at it with aria-describedby. */
  get choiceErrorId(): string {
    return `ee-multi-select-error-${this.field?.field ?? 'field'}`;
  }

  get showChoiceRequiredError(): boolean {
    return !!this.control?.hasError('required') && (this.control.touched || this.control.dirty);
  }

  /**
   * The control's selected stored values, as a list whichever choice type this is: a MULTI_SELECT
   * holds an array and a RADIO a single scalar. The orphan carry-over is identical for both, so it
   * reads through this rather than branching on the type.
   */
  private get selectedChoiceValues(): string[] {
    const value = this.control?.value;
    if (Array.isArray(value)) {
      return value;
    }
    return value === null || value === undefined || value === '' ? [] : [String(value)];
  }

  /**
   * Whether this field renders as a group of choices built from {@link choiceOptions} — a checkbox
   * group (MULTI_SELECT) or a radio group (RADIO). Both share the option list, the orphan carry-over
   * and the required-error treatment; only the write path differs.
   */
  get rendersChoiceGroup(): boolean {
    return this.field?.type === CustomFieldType.MULTI_SELECT || this.field?.type === CustomFieldType.RADIO;
  }

  /**
   * Whether a numeric field shows the `$` prefix.
   *
   * `null`/unset resolves to true, matching `OrgCustomFieldResolver.toFieldDefinition`
   * (`monetary = cf.monetary ?: (cf.type == DECIMAL)`) — so every field authored before the
   * "Display as currency" toggle existed keeps its `$`, and only an explicit `false` drops it.
   */
  get isMonetary(): boolean {
    return this.field?.monetary ?? true;
  }

  /**
   * The mask pattern for the fall-through text input — empty for every type but TEXT.
   *
   * A mask is TEXT-only by design (Req 9.4), and this branch renders more than TEXT: INTEGER,
   * MULTI_TEXT, ADDRESS and TAGS reach it too. Gating on the type rather than on merely having a
   * pattern means a stray `mask` on one of those can never reshape the stored value.
   */
  get textMask(): string {
    return this.field?.type === CustomFieldType.TEXT ? (this.field?.mask || '') : '';
  }

  isMultiSelectOptionSelected(value: string): boolean {
    return this.selectedChoiceValues.includes(value);
  }

  /**
   * Ticks/unticks one option. The control is rewritten in the rendered (authored, then orphaned)
   * order so the stored array doesn't depend on the order the boxes were clicked (Req 5.2), and
   * clearing every box writes an empty array — never null and never '' (Req 1.3).
   */
  toggleMultiSelectOption(value: string, checked: boolean): void {
    if (!this.control || this.control.disabled) {
      return;
    }
    const selected = new Set(this.selectedChoiceValues);
    if (checked) {
      selected.add(value);
    } else {
      selected.delete(value);
    }
    this.control.setValue(this.choiceOptions.filter(o => selected.has(o.value)).map(o => o.value));
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  /**
   * Builds the checkbox list: the field's option definitions (or bare `options`, where label equals
   * value), then any stored value that matches none of them — the option was edited or deleted after
   * the case was filled, and rendering only the current options would let the next unrelated save
   * drop a value the user never touched (Req 5.5).
   */
  private rebuildChoiceOptions(): void {
    if (!this.rendersChoiceGroup) {
      this.choiceOptions = [];
      this.choiceOptionsKey = null;
      return;
    }
    // Copied, never aliased: the orphan merge below appends to this array, and mutating the
    // field's own option_definitions would leak orphans into the field definition.
    // resolveMultiSelectOptions is shared with ee-custom-field and the bulk-update dialog so the
    // three renderers cannot drift on which carrier wins or on the blank-value rule (Req 4.6).
    const defined: CustomFieldOption[] = resolveMultiSelectOptions(this.field);
    // Orphans already on screen are carried across a re-emission of the same field — a new
    // IntakeForm object from the store is enough to land here, and an orphan the user has just
    // UNTICKED is no longer in the value for the merge below to re-find. Dropping its box would
    // strand a value the user can no longer restore (Req 5.5).
    const carried = this.choiceOptionsKey === this.field.field
      ? this.choiceOptions.filter(o => !defined.some(d => d.value === o.value))
      : [];
    this.choiceOptions = [...defined, ...carried];
    this.choiceOptionsKey = this.field.field;
    this.mergeStoredValuesIntoChoiceOptions();
  }

  /**
   * Appends any stored value not already rendered. Only ever adds — unticking an orphaned value must
   * leave its checkbox on screen so it can be ticked back on.
   */
  private mergeStoredValuesIntoChoiceOptions(): void {
    if (!this.rendersChoiceGroup) {
      return;
    }
    const rendered = new Set(this.choiceOptions.map(o => o.value));
    const missing = [...new Set(this.selectedChoiceValues.filter(v => !rendered.has(v)))];
    if (missing.length) {
      this.choiceOptions = [...this.choiceOptions, ...missing.map(value => ({value, label: value}))];
    }
  }

  public get formClass(): string {
    let classes = '';
    if (this.compact) {
      classes = 'compact';
    }
    if (this.hideFromIntake) {
      classes += ' hidden-from-intake';
    }
    return classes;
  }

  protected readonly formType = DocumentType;
  protected readonly fieldTypeToIcon = fieldTypeToIcon;
}
